import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Loader2, TrendingUp, TrendingDown, Trophy, Users, Sparkles } from 'lucide-react';
import { formatClassLabel } from '@/constants/classes';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ClassImprovementDashboard = () => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({ rows: [], summary: {} });

  const load = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/teacher/class-improvement`);
      setData(res.data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  if (loading) {
    return <div className="flex items-center justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-purple-600" /></div>;
  }

  const s = data.summary || {};
  const rows = data.rows || [];

  const colorForDelta = (d) => {
    if (d >= 25) return { bg: 'bg-emerald-100', text: 'text-emerald-800', border: 'border-emerald-300', icon: 'text-emerald-600' };
    if (d >= 10) return { bg: 'bg-green-100', text: 'text-green-800', border: 'border-green-300', icon: 'text-green-600' };
    if (d >= 0)  return { bg: 'bg-yellow-100', text: 'text-yellow-800', border: 'border-yellow-300', icon: 'text-yellow-600' };
    return { bg: 'bg-red-100', text: 'text-red-800', border: 'border-red-300', icon: 'text-red-600' };
  };

  return (
    <div className="space-y-5" data-testid="class-improvement-section">
      {/* Hero Summary — the "+29%" card */}
      <div className="bg-gradient-to-br from-emerald-600 to-teal-700 rounded-2xl p-6 text-white shadow-lg">
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <Sparkles className="w-6 h-6" />
              <h2 className="text-xl font-bold">Class Improvement Summary</h2>
            </div>
            <p className="text-emerald-100 text-sm">
              Baseline (first attempt) → Post (latest attempt) across all your students &amp; chapters
            </p>
          </div>
          <Button variant="secondary" onClick={load} className="bg-white/20 hover:bg-white/30 text-white border-0">Refresh</Button>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-5">
          <div className="bg-white/10 rounded-lg px-4 py-3" data-testid="ci-students">
            <p className="text-xs text-emerald-100 flex items-center gap-1"><Users className="w-3 h-3" /> Students</p>
            <p className="text-3xl font-bold">{s.students || 0}</p>
          </div>
          <div className="bg-white/10 rounded-lg px-4 py-3" data-testid="ci-baseline">
            <p className="text-xs text-emerald-100">Avg Before</p>
            <p className="text-3xl font-bold">{s.avg_baseline ?? 0}%</p>
          </div>
          <div className="bg-white/10 rounded-lg px-4 py-3" data-testid="ci-post">
            <p className="text-xs text-emerald-100">Avg After</p>
            <p className="text-3xl font-bold">{s.avg_post ?? 0}%</p>
          </div>
          <div className={`rounded-lg px-4 py-3 ${(s.avg_improvement || 0) >= 0 ? 'bg-white/30' : 'bg-red-500/40'}`} data-testid="ci-improvement">
            <p className="text-xs text-emerald-100 flex items-center gap-1">
              {(s.avg_improvement || 0) >= 0 ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
              Improvement
            </p>
            <p className="text-3xl font-bold">
              {(s.avg_improvement || 0) >= 0 ? '+' : ''}{s.avg_improvement ?? 0}%
            </p>
          </div>
        </div>
      </div>

      {/* Per-chapter cards */}
      {rows.length === 0 ? (
        <Card className="border-dashed">
          <CardContent className="p-10 text-center text-gray-500">
            Not enough data yet. Students need at least 2 attempts on a chapter to show before/after.
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2">
          <h3 className="font-semibold text-gray-800">Chapter-by-Chapter Breakdown ({rows.length})</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {rows.map((r, i) => {
              const c = colorForDelta(r.avg_improvement);
              return (
                <Card key={`${r.class_level}-${r.section}-${r.chapter}-${i}`} className={`border-2 ${c.border}`} data-testid={`improvement-row-${i}`}>
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between gap-2 mb-2">
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 flex-wrap mb-1">
                          <Badge variant="outline" className="text-xs">{formatClassLabel(r.class_level)}{r.section}</Badge>
                          <Badge variant="secondary" className="text-xs">{r.subject}</Badge>
                          <span className="text-xs text-gray-500">{r.students_count} students</span>
                        </div>
                        <h4 className="font-semibold text-gray-900 text-sm truncate" title={r.chapter}>{r.chapter}</h4>
                      </div>
                      <div className={`px-3 py-1.5 rounded-lg ${c.bg} ${c.text} font-bold text-lg flex items-center gap-1`}>
                        {r.avg_improvement >= 0 ? <TrendingUp className={`w-4 h-4 ${c.icon}`} /> : <TrendingDown className={`w-4 h-4 ${c.icon}`} />}
                        {r.avg_improvement >= 0 ? '+' : ''}{r.avg_improvement}%
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-2 text-sm mt-2 mb-2">
                      <div className="bg-gray-50 rounded-md px-2 py-1.5">
                        <p className="text-[10px] uppercase text-gray-500 tracking-wide">Before</p>
                        <p className="font-bold text-gray-700">{r.avg_baseline}%</p>
                      </div>
                      <div className="bg-emerald-50 rounded-md px-2 py-1.5">
                        <p className="text-[10px] uppercase text-emerald-600 tracking-wide">After</p>
                        <p className="font-bold text-emerald-700">{r.avg_post}%</p>
                      </div>
                    </div>
                    {r.top_improver_name && r.top_improver_delta > 0 && (
                      <div className="flex items-center gap-1.5 text-xs bg-amber-50 rounded-md px-2 py-1 mt-1 border border-amber-100">
                        <Trophy className="w-3.5 h-3.5 text-amber-600" />
                        <span className="text-amber-800">Top improver: <b>{r.top_improver_name}</b></span>
                        <span className="text-amber-600 font-bold">+{r.top_improver_delta}%</span>
                      </div>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default ClassImprovementDashboard;
